// PaymentModal.jsx - Proper PayMongo Payment Intent Integration
import { useState, useEffect } from 'react';
import Card from './ui/Card';
import Button from './ui/Button';
import { api } from '../services/api';

// PayMongo public key - reads from .env, with fallback for development
const PAYMONGO_PUBLIC_KEY = process.env.REACT_APP_PAYMONGO_PUBLIC_KEY || 'pk_test_DnZ6JNurwYRLdCUoPHaTaNnD';

if (!process.env.REACT_APP_PAYMONGO_PUBLIC_KEY && process.env.NODE_ENV === 'development') {
  console.warn('⚠️ REACT_APP_PAYMONGO_PUBLIC_KEY not found in .env, using fallback');
}
const PAYMONGO_API_URL = 'https://api.paymongo.com/v1';

// Only log in development
if (process.env.NODE_ENV === 'development') {
  console.log('💳 PayMongo configured:', PAYMONGO_PUBLIC_KEY ? 'YES' : 'NO');
}

// Helper to encode API key for Basic Auth
const encodeKey = (key) => {
  if (!key) {
    throw new Error('PayMongo public key is not configured. Please set REACT_APP_PAYMONGO_PUBLIC_KEY in your .env file and restart the server.');
  }
  return btoa(key + ':');
};

// PayMongo Payment Form Component
function PaymentForm({ plan, planName, price, onSuccess, onClose, paymentIntentId, clientKey, paymentAmount, billingPeriod }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvc, setCvc] = useState('');
  const [cardholderName, setCardholderName] = useState('');
  const [email, setEmail] = useState('');

  // Format card number with spaces
  const formatCardNumber = (value) => {
    const cleaned = value.replace(/\s/g, '');
    const formatted = cleaned.match(/.{1,4}/g)?.join(' ') || cleaned;
    return formatted.substring(0, 19); // Max 16 digits + 3 spaces
  };

  // Format expiry as MM/YY
  const formatExpiry = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length >= 2) {
      return cleaned.substring(0, 2) + '/' + cleaned.substring(2, 4);
    }
    return cleaned;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    const cleanedCardNumber = cardNumber.replace(/\s/g, '');
    if (cleanedCardNumber.length !== 16) {
      setError('Please enter a valid 16-digit card number');
      return;
    }

    if (!expiry.match(/^\d{2}\/\d{2}$/)) {
      setError('Please enter expiry in MM/YY format');
      return;
    }

    if (cvc.length !== 3) {
      setError('Please enter a valid 3-digit CVC');
      return;
    }

    if (!cardholderName.trim()) {
      setError('Please enter cardholder name');
      return;
    }

    if (!email.trim() || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('💳 Step 1: Creating Payment Method with PayMongo...');
      
      const [expMonth, expYear] = expiry.split('/');

      // Step 1: Create Payment Method (client-side with public key)
      const paymentMethodResponse = await fetch(`${PAYMONGO_API_URL}/payment_methods`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${encodeKey(PAYMONGO_PUBLIC_KEY)}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          data: {
            attributes: {
              type: 'card',
              details: {
                card_number: cleanedCardNumber,
                exp_month: parseInt(expMonth),
                exp_year: parseInt('20' + expYear),
                cvc: cvc
              },
              billing: {
                name: cardholderName,
                email: email
              }
            }
          }
        })
      });

      const paymentMethodData = await paymentMethodResponse.json();

      if (!paymentMethodResponse.ok) {
        const errorMsg = paymentMethodData.errors?.[0]?.detail || 'Failed to create payment method';
        throw new Error(errorMsg);
      }

      const paymentMethodId = paymentMethodData.data.id;
      console.log('✅ Payment Method created:', paymentMethodId);

      // Step 2: Attach Payment Method to Payment Intent
      console.log('💳 Step 2: Attaching payment method to payment intent...');
      
      const attachResponse = await fetch(
        `${PAYMONGO_API_URL}/payment_intents/${paymentIntentId}/attach`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Basic ${encodeKey(PAYMONGO_PUBLIC_KEY)}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            data: {
              attributes: {
                payment_method: paymentMethodId,
                client_key: clientKey,
                return_url: `${window.location.origin}/payment-success?plan=${plan}&billing=${billingPeriod}`
              }
            }
          })
        }
      );

      const attachData = await attachResponse.json();

      if (!attachResponse.ok) {
        const errorMsg = attachData.errors?.[0]?.detail || 'Failed to attach payment method';
        throw new Error(errorMsg);
      }

      const paymentIntent = attachData.data;
      const status = paymentIntent.attributes.status;

      console.log('✅ Payment Method attached. Status:', status);

      // Step 3: Handle different payment statuses
      if (status === 'awaiting_next_action') {
        // Redirect for 3D Secure authentication
        const redirectUrl = paymentIntent.attributes.next_action?.redirect?.url;
        if (redirectUrl) {
          console.log('🔐 Redirecting for 3D Secure authentication...');
          window.location.href = redirectUrl;
        } else {
          throw new Error('No redirect URL provided for authentication');
        }
      } else if (status === 'succeeded') {
        // Payment succeeded immediately (rare for cards, common for some e-wallets)
        console.log('✅ Payment succeeded immediately!');
        await confirmPaymentOnBackend(paymentIntentId, billingPeriod);
        onSuccess(plan);
        onClose();
      } else if (status === 'processing') {
        // Payment is processing, wait and check status
        console.log('⏳ Payment processing...');
        setTimeout(async () => {
          await checkPaymentStatus(paymentIntentId, clientKey);
        }, 2000);
      } else {
        // Payment failed or other status
        const lastError = paymentIntent.attributes.last_payment_error;
        throw new Error(lastError || `Payment failed with status: ${status}`);
      }

    } catch (err) {
      console.error('❌ Payment error:', err);
      setError(err.message || 'Payment failed. Please try again.');
      setLoading(false);
    }
  };

  // Check payment status after processing
  const checkPaymentStatus = async (paymentIntentId, clientKey) => {
    try {
      const response = await fetch(
        `${PAYMONGO_API_URL}/payment_intents/${paymentIntentId}?client_key=${clientKey}`,
        {
          headers: {
            'Authorization': `Basic ${encodeKey(PAYMONGO_PUBLIC_KEY)}`
          }
        }
      );

      const data = await response.json();
      const status = data.data.attributes.status;

      if (status === 'succeeded') {
        await confirmPaymentOnBackend(paymentIntentId, billingPeriod);
        onSuccess(plan);
        onClose();
      } else if (status === 'processing') {
        // Still processing, check again
        setTimeout(() => checkPaymentStatus(paymentIntentId, clientKey), 2000);
      } else {
        throw new Error('Payment was not completed');
      }
    } catch (err) {
      setError(err.message || 'Failed to verify payment');
      setLoading(false);
    }
  };

  // Confirm payment with backend
  const confirmPaymentOnBackend = async (paymentIntentId, billingPeriod) => {
    try {
      const confirmResponse = await api('/api/confirm-payment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          payment_intent_id: paymentIntentId,
          billingPeriod: billingPeriod
        })
      });

      if (!confirmResponse || !confirmResponse.ok) {
        throw new Error(confirmResponse?.error || 'Failed to confirm payment');
      }

      console.log('✅ Payment confirmed on backend');
    } catch (err) {
      console.error('❌ Backend confirmation error:', err);
      throw err;
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
        <div className="flex justify-between items-center mb-4">
          <span className="font-semibold">{planName} Plan</span>
          <span className="text-2xl font-bold">₱{price}</span>
        </div>
        
        <div className="space-y-3">
          {/* Email */}
          <div>
            <label className="block text-sm font-medium mb-1">Email</label>
            <input
              type="email"
              placeholder="your.email@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 focus:ring-2 focus:ring-blue-500"
              disabled={loading}
              required
            />
          </div>

          {/* Card Number */}
          <div>
            <label className="block text-sm font-medium mb-1">Card Number</label>
            <input
              type="text"
              placeholder="1234 5678 9012 3456"
              value={cardNumber}
              onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 focus:ring-2 focus:ring-blue-500"
              disabled={loading}
              required
            />
          </div>

          {/* Cardholder Name */}
          <div>
            <label className="block text-sm font-medium mb-1">Cardholder Name</label>
            <input
              type="text"
              placeholder="NAME ON CARD"
              value={cardholderName}
              onChange={(e) => setCardholderName(e.target.value.toUpperCase())}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 focus:ring-2 focus:ring-blue-500"
              disabled={loading}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            {/* Expiry */}
            <div>
              <label className="block text-sm font-medium mb-1">Expiry</label>
              <input
                type="text"
                placeholder="MM/YY"
                value={expiry}
                onChange={(e) => setExpiry(formatExpiry(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 focus:ring-2 focus:ring-blue-500"
                disabled={loading}
                required
              />
            </div>

            {/* CVC */}
            <div>
              <label className="block text-sm font-medium mb-1">CVC</label>
              <input
                type="text"
                placeholder="123"
                value={cvc}
                onChange={(e) => setCvc(e.target.value.replace(/\D/g, '').substring(0, 3))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 focus:ring-2 focus:ring-blue-500"
                disabled={loading}
                required
              />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-3 rounded-lg">
        <p className="text-green-800 dark:text-green-200 text-xs flex items-center gap-2">
          <span>🔒</span>
          <span><strong>Secure Payment:</strong> Your payment information is encrypted and processed securely. We accept Visa, Mastercard, and other major credit cards.</span>
        </p>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 rounded-lg">
          <p className="text-red-800 dark:text-red-200 text-sm">❌ {error}</p>
        </div>
      )}

      <div className="flex space-x-3 w-full">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          className="flex-1"
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          disabled={loading}
          className="flex-1"
        >
          {loading ? 'Processing...' : `Pay ₱${price}`}
        </Button>
      </div>
    </form>
  );
}

export default function PaymentModal({ 
  isOpen, 
  onClose, 
  plan, 
  planName, 
  price, 
  currentPlan,
  onSuccess,
  billingPeriod = 'monthly'
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentIntentId, setPaymentIntentId] = useState('');
  const [clientKey, setClientKey] = useState('');
  const [paymentAmount, setPaymentAmount] = useState(0);

  const createPaymentIntent = async () => {
    setLoading(true);
    setError('');
    
    try {
      console.log('🎯 Creating PayMongo payment intent for plan:', plan, 'billing:', billingPeriod);
      
      const response = await api('/api/create-payment-intent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          plan, 
          billingPeriod: billingPeriod 
        })
      });

      if (response && response.ok && response.paymentIntentId) {
        setPaymentIntentId(response.paymentIntentId);
        setClientKey(response.clientSecret);
        setPaymentAmount(response.amount); // Store the amount in centavos
        console.log('✅ Payment intent created:', response.paymentIntentId);
      } else {
        setError(response?.error || 'Failed to initialize payment');
        console.error('Invalid response:', response);
      }
    } catch (err) {
      console.error('❌ Payment intent error:', err);
      setError('Failed to initialize payment: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && plan !== 'free' && plan !== currentPlan) {
      createPaymentIntent();
    }
    
    // Reset state when modal closes
    if (!isOpen) {
      setPaymentIntentId('');
      setClientKey('');
      setPaymentAmount(0);
      setError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, plan, currentPlan, billingPeriod]);

  const handleDowngrade = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await api('/api/downgrade-plan', {
        method: 'POST'
      });
      
      if (response && response.ok) {
        console.log('✅ Downgrade successful');
        onSuccess('free');
        onClose();
      } else {
        throw new Error(response?.error || 'Failed to downgrade plan');
      }
    } catch (err) {
      console.error('❌ Downgrade error:', err);
      setError(err.message || 'Downgrade failed');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // Handle free plan downgrade
  if (plan === 'free') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <Card className="w-full max-w-md">
          <Card.Header>
            <Card.Title>Downgrade Plan</Card.Title>
            <Card.Description>Downgrade to the free plan</Card.Description>
          </Card.Header>

          <Card.Content className="space-y-4">
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 p-4 rounded-lg">
              <p className="text-yellow-800 dark:text-yellow-200 text-sm">
                ⚠️ Downgrading to free will limit you to 3 websites and basic features.
              </p>
            </div>

            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 rounded-lg">
                <p className="text-red-800 dark:text-red-200 text-sm">❌ {error}</p>
              </div>
            )}
          </Card.Content>

          <Card.Footer>
            <div className="flex space-x-3 w-full">
              <Button
                variant="outline"
                onClick={onClose}
                className="flex-1"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="outline"
                onClick={handleDowngrade}
                disabled={loading}
                className="flex-1"
              >
                {loading ? 'Processing...' : 'Confirm Downgrade'}
              </Button>
            </div>
          </Card.Footer>
        </Card>
      </div>
    );
  }

  // Handle paid plan upgrade
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
        <Card.Header>
          <Card.Title>Upgrade to {planName}</Card.Title>
          <Card.Description>
            {billingPeriod === 'annual' ? 'Annual billing - Save 17%!' : 'Monthly billing'}
          </Card.Description>
        </Card.Header>

        <Card.Content>
          {loading && !paymentIntentId ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Preparing payment...</p>
            </div>
          ) : error && !paymentIntentId ? (
            <div className="space-y-4">
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 rounded-lg">
                <p className="text-red-800 dark:text-red-200 text-sm">❌ {error}</p>
              </div>
              <div className="flex space-x-3 w-full">
                <Button variant="outline" onClick={onClose} className="flex-1">
                  Cancel
                </Button>
                <Button variant="primary" onClick={createPaymentIntent} className="flex-1">
                  Retry
                </Button>
              </div>
            </div>
          ) : (
            <PaymentForm
              plan={plan}
              planName={planName}
              price={price}
              onSuccess={onSuccess}
              onClose={onClose}
              paymentIntentId={paymentIntentId}
              clientKey={clientKey}
              paymentAmount={paymentAmount}
              billingPeriod={billingPeriod}
            />
          )}
        </Card.Content>
      </Card>
    </div>
  );
}
